import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, PackageCheck, Plus, Search, ShoppingBag, Wallet, X } from 'lucide-react'
import {
  formatDateTime, formatMoney, formatNumber, PAYMENT_METHODS, PERMISSIONS, PURCHASE_STATUSES,
  supplierPaymentSchema, toMinor, type PurchaseStatus,
} from '@najah/core'
import {
  Alert, Badge, Button, ConfirmDialog, Dialog, EmptyState, Input, Pagination, Select, Switch,
  Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { purchasesApi, suppliersApi, supplierPaymentsApi, type PurchaseFilter } from '../lib/purchasingApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'
import { PurchaseFormDialog } from '../components/purchasing/PurchaseFormDialog'

const STATUS_TONE: Record<PurchaseStatus, 'success' | 'warning' | 'danger'> = {
  RECEIVED: 'success',
  DRAFT: 'warning',
  CANCELLED: 'danger',
}

function PaymentDialog({
  purchaseId, supplierId, balance, onClose,
}: { purchaseId: string | null; supplierId: string; balance: number; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, validate, handleApiError } = useFormErrors()
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState('CASH')
  const [docNumber, setDocNumber] = useState('')
  const [fromDrawer, setFromDrawer] = useState(false)

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = validate(supplierPaymentSchema, {
        supplierId,
        purchaseId,
        direction: 'OUT',
        method,
        amount: toMinor(Number(amount) || 0),
        docNumber: docNumber.trim(),
        fromDrawer,
      })
      if (!parsed) throw new Error('VALIDATION')
      return supplierPaymentsApi.create(parsed)
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['purchases'] })
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      void queryClient.invalidateQueries({ queryKey: ['shift'] })
      toast.success('سُجّلت الدفعة', `${res.reference} — ${formatMoney(res.amount)}`)
      setAmount('')
      setFromDrawer(false)
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر تسجيل الدفعة')
    },
  })

  return (
    <Dialog
      open={!!purchaseId}
      onClose={onClose}
      size="sm"
      title="دفعة للمورّد"
      description={`المتبقّي على الفاتورة ${formatMoney(balance)}`}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>تسجيل الدفعة</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <Input
          label="المبلغ" required type="number" min="0" step="0.01" ltr autoFocus
          value={amount} onChange={(e) => setAmount(e.target.value)}
          error={errors.amount} hint="دج"
        />
        <button
          type="button"
          onClick={() => setAmount(String(balance / 100))}
          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:bg-surface-hover"
        >
          المبلغ كاملاً
        </button>

        <Select
          label="طريقة الدفع"
          value={method} onChange={(e) => setMethod(e.target.value)}
          options={Object.entries(PAYMENT_METHODS)
            .filter(([k]) => k !== 'CREDIT')
            .map(([value, label]) => ({ value, label }))}
        />

        <Input
          label="رقم الشيك أو الحوالة" ltr
          value={docNumber} onChange={(e) => setDocNumber(e.target.value)}
          error={errors.docNumber}
        />

        <Switch
          checked={fromDrawer}
          onChange={(e) => setFromDrawer(e.target.checked)}
          disabled={method !== 'CASH'}
          label="خصم من درج الصندوق"
          description={method !== 'CASH' ? 'متاح للدفع النقدي فقط' : 'يتطلّب وردية مفتوحة'}
        />
      </div>
    </Dialog>
  )
}

export function PurchasesPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.PURCHASE_MANAGE)

  const [filter, setFilter] = useState<PurchaseFilter>({ page: 1, pageSize: 25 })
  const [searchInput, setSearchInput] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [paying, setPaying] = useState<{ id: string; supplierId: string; balance: number } | null>(null)
  const [cancelling, setCancelling] = useState<string | null>(null)

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['purchases', filter],
    queryFn: () => purchasesApi.list(filter),
    placeholderData: keepPreviousData,
  })

  const { data: suppliers } = useQuery({ queryKey: ['suppliers', 'options'], queryFn: suppliersApi.options })

  const { data: detail } = useQuery({
    queryKey: ['purchases', 'detail', detailId],
    queryFn: () => purchasesApi.get(detailId!),
    enabled: !!detailId,
  })

  const receiveMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.receive(id),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['purchases'] })
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      toast.success('تمّ الاستلام', `${res.reference} — دخلت البضاعة للمخزون`)
    },
    onError: (err) => toast.error('تعذّر الاستلام', err instanceof Error ? err.message : undefined),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.cancel(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['purchases'] })
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      toast.success('أُلغيت الفاتورة')
      setCancelling(null)
    },
    onError: (err) => {
      toast.error('تعذّر الإلغاء', err instanceof Error ? err.message : undefined)
      setCancelling(null)
    },
  })

  function patch(next: PurchaseFilter) {
    setFilter((f) => ({ ...f, ...next, page: next.page ?? 1 }))
  }

  const totalDue = data?.items.reduce((sum, p) => sum + (p.status !== 'CANCELLED' ? p.balance : 0), 0) ?? 0

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">المشتريات</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {data ? `${formatNumber(data.total)} فاتورة` : 'جارٍ التحميل…'}
            {totalDue > 0 && (
              <> · متبقٍّ <span className="font-medium text-warning" dir="ltr">{formatMoney(totalDue)}</span></>
            )}
          </p>
        </div>
        {canManage && (
          <Button startIcon={<Plus className="size-4" />} onClick={() => setFormOpen(true)}>
            فاتورة شراء
          </Button>
        )}
      </div>

      <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 sm:grid-cols-4">
        <form
          className="sm:col-span-2"
          onSubmit={(e) => { e.preventDefault(); patch({ search: searchInput.trim() || undefined }) }}
        >
          <Input
            label="بحث"
            placeholder="رقم الفاتورة أو رقم فاتورة المورّد…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            startIcon={<Search className="size-4" />}
            endIcon={
              searchInput ? (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); patch({ search: undefined }) }}
                  className="pointer-events-auto rounded p-0.5 text-fg-subtle hover:text-fg"
                  aria-label="مسح"
                >
                  <X className="size-4" />
                </button>
              ) : undefined
            }
          />
        </form>
        <Select
          label="المورّد"
          value={filter.supplierId ?? ''}
          onChange={(e) => patch({ supplierId: e.target.value || undefined })}
          options={(suppliers ?? []).map((s) => ({ value: s.id, label: s.nameAr }))}
          placeholder="كل الموردين"
        />
        <Select
          label="الحالة"
          value={filter.status ?? ''}
          onChange={(e) => patch({ status: (e.target.value || undefined) as PurchaseStatus | undefined })}
          options={Object.entries(PURCHASE_STATUSES).map(([value, label]) => ({ value, label }))}
          placeholder="كل الحالات"
        />
      </div>

      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th>رقم الفاتورة</Th>
              <Th>المورّد</Th>
              <Th>التاريخ</Th>
              <Th align="center">الأصناف</Th>
              <Th align="end">الإجمالي</Th>
              <Th align="end">المتبقّي</Th>
              <Th>الحالة</Th>
              <Th align="center">إجراءات</Th>
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={6} cols={8} />
          ) : (
            <Tbody>
              {data?.items.map((p) => (
                <Tr key={p.id}>
                  <Td>
                    <button
                      type="button"
                      onClick={() => setDetailId(p.id)}
                      className="font-mono text-xs font-medium text-primary hover:underline"
                      dir="ltr"
                    >
                      {p.reference}
                    </button>
                    {p.invoiceNumber && (
                      <p className="text-[11px] text-fg-subtle" dir="ltr">مورّد: {p.invoiceNumber}</p>
                    )}
                  </Td>
                  <Td className="font-medium text-fg">{p.supplierName}</Td>
                  <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(p.createdAt)}</Td>
                  <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(p.itemCount)}</Td>
                  <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(p.total)}</Td>
                  <Td align="end" className="tabular-nums" dir="ltr">
                    <span className={p.balance > 0 ? 'text-warning' : 'text-success'}>{formatMoney(p.balance)}</span>
                  </Td>
                  <Td><Badge tone={STATUS_TONE[p.status]}>{PURCHASE_STATUSES[p.status]}</Badge></Td>
                  <Td align="center">
                    <div className="flex items-center justify-center gap-1">
                      {canManage && p.status === 'DRAFT' && (
                        <Button
                          variant="ghost" size="icon" title="استلام"
                          onClick={() => receiveMutation.mutate(p.id)}
                          loading={receiveMutation.isPending && receiveMutation.variables === p.id}
                        >
                          <PackageCheck className="size-4" />
                        </Button>
                      )}
                      {canManage && p.status !== 'CANCELLED' && p.balance > 0 && (
                        <Button
                          variant="ghost" size="icon" title="دفعة"
                          onClick={() => setPaying({ id: p.id, supplierId: p.supplierId, balance: p.balance })}
                        >
                          <Wallet className="size-4" />
                        </Button>
                      )}
                      {canManage && p.status !== 'CANCELLED' && (
                        <Button
                          variant="ghost" size="icon" title="إلغاء"
                          className="text-danger hover:bg-danger-subtle"
                          onClick={() => setCancelling(p.id)}
                        >
                          <Ban className="size-4" />
                        </Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState
            icon={<ShoppingBag className="size-10" />}
            title="لا توجد فواتير شراء"
            description="سجّل فواتير المورّدين ليدخل المخزون وتُحسب التكلفة"
            action={canManage ? <Button onClick={() => setFormOpen(true)}>فاتورة شراء</Button> : undefined}
          />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            pageSize={data.pageSize}
            onPageChange={(page) => setFilter((f) => ({ ...f, page }))}
            onPageSizeChange={(pageSize) => patch({ pageSize })}
          />
        )}
      </TableWrapper>

      <PurchaseFormDialog open={formOpen} onClose={() => setFormOpen(false)} />

      {/* ── تفاصيل الفاتورة ── */}
      <Dialog
        open={!!detailId}
        onClose={() => setDetailId(null)}
        size="xl"
        title={detail?.reference}
        description={detail ? `${detail.supplierName} · ${formatDateTime(detail.createdAt)}` : undefined}
      >
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-bg-subtle p-4 text-sm sm:grid-cols-4">
              <div>
                <p className="text-xs text-fg-subtle">الحالة</p>
                <Badge tone={STATUS_TONE[detail.status]}>{PURCHASE_STATUSES[detail.status]}</Badge>
              </div>
              <div>
                <p className="text-xs text-fg-subtle">المدفوع</p>
                <p className="font-medium text-fg" dir="ltr">{formatMoney(detail.paidTotal)}</p>
              </div>
              <div>
                <p className="text-xs text-fg-subtle">المتبقّي</p>
                <p className="font-medium text-warning" dir="ltr">{formatMoney(detail.balance)}</p>
              </div>
              <div>
                <p className="text-xs text-fg-subtle">أدخلها</p>
                <p className="font-medium text-fg">{detail.userName ?? '—'}</p>
              </div>
            </div>

            <TableWrapper>
              <Table>
                <Thead>
                  <tr>
                    <Th>الصنف</Th>
                    <Th align="center">الكمية</Th>
                    <Th align="end">تكلفة الوحدة</Th>
                    <Th align="end">الإجمالي</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {detail.items.map((item) => (
                    <Tr key={item.id}>
                      <Td>
                        <p className="font-medium text-fg">{item.nameSnapshot}</p>
                        <p className="font-mono text-xs text-fg-subtle" dir="ltr">{item.skuSnapshot}</p>
                      </Td>
                      <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(item.quantity)}</Td>
                      <Td align="end" className="tabular-nums" dir="ltr">{formatMoney(item.unitCost)}</Td>
                      <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(item.lineTotal)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableWrapper>

            <div className="space-y-1 border-t border-border pt-3 text-sm">
              <div className="flex justify-between text-fg-muted">
                <span>مجموع الأصناف</span>
                <span dir="ltr">{formatMoney(detail.subtotal)}</span>
              </div>
              {detail.shippingCost > 0 && (
                <div className="flex justify-between text-fg-muted">
                  <span>النقل (موزَّع على الأصناف)</span>
                  <span dir="ltr">{formatMoney(detail.shippingCost)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-2 text-lg font-bold text-fg">
                <span>الإجمالي</span>
                <span dir="ltr">{formatMoney(detail.total)}</span>
              </div>
            </div>

            {detail.payments.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-fg-muted">الدفعات</h3>
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {detail.payments.map((pay) => (
                    <li key={pay.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="font-mono text-xs text-fg-muted" dir="ltr">{pay.reference}</span>
                      <span className="text-xs text-fg-subtle">{formatDateTime(pay.paidAt)}</span>
                      <span className="font-semibold tabular-nums text-fg" dir="ltr">{formatMoney(pay.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Dialog>

      <PaymentDialog
        purchaseId={paying?.id ?? null}
        supplierId={paying?.supplierId ?? ''}
        balance={paying?.balance ?? 0}
        onClose={() => setPaying(null)}
      />

      <ConfirmDialog
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        onConfirm={() => cancelling && cancelMutation.mutate(cancelling)}
        loading={cancelMutation.isPending}
        title="إلغاء فاتورة الشراء"
        confirmLabel="إلغاء الفاتورة"
        cancelLabel="تراجع"
        description={
          <>
            إن كانت الفاتورة مُستلَمة فستُسجَّل حركة عكسية تُخرج البضاعة من المخزون.
            <br />
            الحركة الأصلية تبقى في السجل — لا يُمحى شيء.
          </>
        }
      />
    </div>
  )
}
